import { useState } from 'react';
import CameraCapture from '../components/CameraCapture';
import { useExpenses } from '../contexts/ExpenseContext';
import { useSettings } from '../contexts/SettingsContext';
import { BENNETT_EMAILS } from '../types';
import { 
  CheckCircle, 
  Loader2,
  AlertCircle,
  ChevronLeft,
  Send,
  Save,
  Camera
} from 'lucide-react';

export default function BOLSend() {
  const { addExpense } = useExpenses();
  const { settings } = useSettings();
  
  const [step, setStep] = useState<'camera' | 'details' | 'success'>('camera');
  const [capturedImage, setCapturedImage] = useState<{ blob: Blob; filename: string } | null>(null);
  const [freightBillNumber, setFreightBillNumber] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(BENNETT_EMAILS[0].email);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [lastAction, setLastAction] = useState<'save' | 'send' | 'both'>('both');

  const handleCapture = (blob: Blob, filename: string) => {
    setCapturedImage({ blob, filename });
    setStep('details');
  };

  const handleCancel = () => {
    setStep('camera');
    setCapturedImage(null);
  };

  const generateEmailLink = () => {
    const subject = encodeURIComponent(`Freight Bill# ${freightBillNumber}`);
    const body = encodeURIComponent(
      `Delivered\nTruck# ${settings.truckNumber}\nDriver ID: ${settings.driverId}\nSent from Tax Expense Tracker`
    );
    return `mailto:${selectedEmail}?subject=${subject}&body=${body}`;
  };

  const handleSave = async () => {
    if (!capturedImage) return;

    setIsSubmitting(true);
    setError('');

    try {
      const bolFilename = `BOL${freightBillNumber}_${date.replace(/-/g, '')}.jpg`;
      
      await addExpense({
        category: 'bol',
        description: `Freight Bill# ${freightBillNumber}`,
        date,
        file: capturedImage.blob,
        filename: bolFilename,
        freightBillNumber,
      });

      return true;
    } catch (err) {
      setError('Failed to save BOL. Please try again.');
      console.error(err);
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveOnly = async () => {
    setLastAction('save');
    const success = await handleSave();
    if (success) {
      setStep('success');
    }
  };

  const handleSendOnly = () => {
    setLastAction('send');
    window.location.href = generateEmailLink();
  };

  const handleSaveAndSend = async () => {
    setLastAction('both');
    const success = await handleSave();
    if (success) {
      // Open email client
      window.open(generateEmailLink(), '_blank');
      setStep('success');
    }
  };

  const resetForm = () => {
    setStep('camera');
    setCapturedImage(null);
    setFreightBillNumber('');
    setDate(new Date().toISOString().split('T')[0]);
    setError('');
  };

  if (step === 'success') {
    return (
      <div className="max-w-md mx-auto text-center py-20 animate-fade-in">
        <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <CheckCircle className="w-10 h-10 text-green-400" />
        </div>
        <h2 className="text-2xl font-bold text-white mb-2">
          {lastAction === 'both' ? 'Saved & Email Opened!' : 'BOL Saved!'}
        </h2>
        <p className="text-white/60 mb-6">
          {lastAction === 'both' 
            ? 'Attach the image in your email app to send to Bennett'
            : 'BOL has been saved to your expenses'}
        </p>
        <button
          onClick={resetForm}
          className="inline-flex items-center space-x-2 px-6 py-3 bg-shuttle-sand text-shuttle-dark rounded-xl font-medium hover:bg-amber-400 transition-colors"
        >
          <Camera className="w-5 h-5" />
          <span>Scan Another BOL</span>
        </button>
      </div>
    );
  }

  if (step === 'camera') {
    return (
      <div className="max-w-2xl mx-auto animate-fade-in">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Scan BOL</h1>
          <p className="text-white/60 mt-1">Scan your Bill of Lading to save and email to Bennett</p>
        </div>
        <CameraCapture onCapture={handleCapture} onCancel={() => window.history.back()} />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in">
      <button
        onClick={handleCancel}
        className="flex items-center space-x-2 text-white/70 hover:text-white mb-6 transition-colors"
      >
        <ChevronLeft className="w-5 h-5" />
        <span>Retake Photo</span>
      </button>

      <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
        {/* Image Preview */}
        {capturedImage && (
          <div className="aspect-video bg-black flex items-center justify-center">
            <img
              src={URL.createObjectURL(capturedImage.blob)}
              alt="Captured BOL"
              className="max-h-full max-w-full object-contain"
            />
          </div>
        )}

        {/* Form */}
        <div className="p-6 space-y-6">
          {error && (
            <div className="flex items-center space-x-2 p-4 bg-red-500/20 border border-red-500/50 rounded-xl text-red-300 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Send To Selector */}
          <div>
            <label className="block text-sm font-medium text-white/80 mb-2">
              Send To
            </label>
            <select
              value={selectedEmail}
              onChange={(e) => setSelectedEmail(e.target.value)}
              className="w-full px-4 py-3 bg-shuttle-dark border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand transition-all"
            >
              {BENNETT_EMAILS.map((item) => (
                <option key={item.code} value={item.email} className="bg-shuttle-dark text-white">
                  {item.label} ({item.email})
                </option>
              ))}
            </select>
          </div>

          {/* Email Preview */}
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            <h3 className="text-sm font-medium text-white/80 mb-3">📧 Email Preview</h3>
            <div className="space-y-2 text-sm">
              <p className="text-white/60">
                <span className="text-white/40">To:</span> {selectedEmail}
              </p>
              <p className="text-white/60">
                <span className="text-white/40">Subject:</span> Freight Bill# {freightBillNumber || '________'}
              </p>
              <div className="border-t border-white/10 pt-2 mt-2">
                <p className="text-white/80">Delivered</p>
                <p className="text-white/80">Truck# {settings.truckNumber}</p>
                <p className="text-white/80">Driver ID: {settings.driverId}</p>
              </div>
            </div>
          </div>

          {/* Freight Bill Number */}
          <div>
            <label htmlFor="freightBill" className="block text-sm font-medium text-white/80 mb-1">
              Freight Bill Number <span className="text-red-400">*</span>
            </label>
            <input
              id="freightBill"
              type="text"
              value={freightBillNumber}
              onChange={(e) => setFreightBillNumber(e.target.value)}
              placeholder="e.g., 9954711"
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all text-lg font-mono"
              required
            />
          </div>

          {/* Date */}
          <div>
            <label htmlFor="date" className="block text-sm font-medium text-white/80 mb-1">
              Date
            </label>
            <input
              id="date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:ring-2 focus:ring-shuttle-sand focus:border-shuttle-sand transition-all"
            />
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleSaveAndSend}
              disabled={!freightBillNumber || isSubmitting}
              className="w-full flex items-center justify-center space-x-2 py-4 px-6 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-xl font-medium hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  <span>Save & Send Email</span>
                </>
              )}
            </button>

            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={handleSaveOnly}
                disabled={!freightBillNumber || isSubmitting}
                className="flex items-center justify-center space-x-2 py-3 px-4 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                <span>Save Only</span>
              </button>

              <button
                onClick={handleSendOnly}
                disabled={!freightBillNumber}
                className="flex items-center justify-center space-x-2 py-3 px-4 bg-white/10 border border-white/20 text-white rounded-xl font-medium hover:bg-white/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Send className="w-4 h-4" />
                <span>Send Only</span>
              </button>
            </div>
          </div>

          <p className="text-xs text-white/40 text-center">
            💡 Tip: "Save & Send" will save the BOL and open your email app. 
            You'll need to attach the image manually in your email app.
          </p>
        </div>
      </div>
    </div>
  );
}





